import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  ESupportMessageStatus,
  GetSupportMessagesParams,
  PaginatedResponse,
  Pagination,
  SupportMessage,
  SupportMessageListItem,
  typicodeApi,
} from 'shared/api';

type TSupportMessageState = {
  messageList: SupportMessageListItem[];
  pagination: Pagination | null;
  newCount: number;
};

const initialState: TSupportMessageState = {
  messageList: [],
  pagination: null,
  newCount: 0,
};

export const supportMessageModel = createSlice({
  name: 'supportMessages',
  initialState,
  reducers: {
    setMessageList: (state, { payload }: PayloadAction<SupportMessageListItem[]>) => {
      state.messageList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    setNewCount: (state, { payload }: PayloadAction<number>) => {
      state.newCount = payload;
    },
  },
});

export const SUPPORT_MESSAGES_QUERY_KEY = 'support-messages-all';
const SUPPORT_MESSAGE_BY_ID_QUERY_KEY = 'support-message-by-id';
const SUPPORT_MESSAGES_NEW_COUNT_KEY = 'support-messages-new-count';

export const getSupportMessagesAsync = (params: GetSupportMessagesParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<SupportMessageListItem>>>(
    [SUPPORT_MESSAGES_QUERY_KEY, params],
    () => typicodeApi.supportMessages.getSupportMessages(params),
    {
      onSuccess: ({ data }) => {
        dispatch(supportMessageModel.actions.setMessageList(data.data));
        dispatch(supportMessageModel.actions.setPagination(data.pagination));
      },
      onError: () => {
        toast.error('Error loading support messages. Try reloading the page!');
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

export const getSupportMessageByIdAsync = (id: string, enabled: boolean = true) =>
  useQuery<AxiosResponse<SupportMessage>>(
    [SUPPORT_MESSAGE_BY_ID_QUERY_KEY, id],
    () => typicodeApi.supportMessages.getSupportMessageById(id),
    {
      enabled,
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: false,
    },
  );

export const replySupportMessageAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, replyText }: { id: string; replyText: string }) =>
      typicodeApi.supportMessages.replySupportMessage(id, { replyText }),
    {
      onSuccess: () => {
        toast.success('Reply sent successfully!');
        void queryClient.invalidateQueries(SUPPORT_MESSAGES_QUERY_KEY);
        void queryClient.invalidateQueries(SUPPORT_MESSAGE_BY_ID_QUERY_KEY);
        void queryClient.invalidateQueries(SUPPORT_MESSAGES_NEW_COUNT_KEY);
      },
      onError: () => {
        toast.error('Error sending reply. Try again!');
      },
    },
  );
};

export const getNewMessagesCountAsync = () => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<SupportMessageListItem>>>(
    [SUPPORT_MESSAGES_NEW_COUNT_KEY],
    () => typicodeApi.supportMessages.getSupportMessages({ status: ESupportMessageStatus.New, limit: 1, page: 1 }),
    {
      onSuccess: ({ data }) => {
        dispatch(supportMessageModel.actions.setNewCount(data.pagination.total));
      },
      refetchOnWindowFocus: false,
      refetchInterval: 60000,
    },
  );

export const useSupportMessages = () => useSelector((state: RootState) => state.supportMessages);
export const useSupportMessageList = () =>
  useSelector((state: RootState) => ({
    messageList: state.supportMessages.messageList,
    pagination: state.supportMessages.pagination,
  }));
export const useNewMessagesCount = () => useSelector((state: RootState) => state.supportMessages.newCount);

export const reducer = supportMessageModel.reducer;
